import { call, CallEffect, put, PutEffect, select, SelectEffect } from 'redux-saga/effects';
import { PRODUCT_BOX_STYLE_TYPE } from 'b2b-components/theme/components/ProductList';
import { analyzeEvent } from 'b2b-apps/core/analytics/redux/tealium/actionCreators';
import { TEALIUM_INTERACTION_ID, TEALIUM_PROD_LIST } from 'b2b-apps/core/analytics/types/types';
import { getQuery, getSearch } from 'b2b-apps/core/catalog/redux/search/selectors';
import { CatalogSearchType } from 'b2b-apps/core/catalog/redux/search/types';
import { RemoteRecord } from 'b2b-apps/core/common/redux/RemoteRecord';
import { getProductsBySkus, getProductTileBySku } from 'b2b-apps/core/product/redux/products/tile/selectors';
import { ProductTileType } from 'b2b-apps/core/product/redux/products/tile/types';
import { DEFAULT_PER_PAGE } from 'b2b-apps/core/settings/redux/reducer';
import { getCatalogSettings } from 'b2b-apps/core/settings/redux/selectors';
import { SettingsCatalogType } from 'b2b-apps/core/settings/redux/types';
import { OSS_INTERACTION_PARAMETERS } from '../../search/types';
import { getSearchAnalyticsOssData } from '../../search/utils';
import { AnalyzeProductsInteraction } from '../types';
import { createProductsListsAnalyticsEventData, getInteractionProduct } from '../utils';
import { analyzeProductAddToCartFromListInteraction, analyzeProductClickInteraction } from './interactions';

export function* analyzeCatalogProductListView():
  Generator<
    PutEffect | SelectEffect,
    void,
    & RemoteRecord<CatalogSearchType>
    & Record<string, RemoteRecord<ProductTileType>>
    & SettingsCatalogType
    > {
  const catalogRecord = yield select(getSearch);
  const catalog = catalogRecord.data;

  if (!catalog) {
    return null;
  }

  const productTiles = yield select(getProductsBySkus, catalogRecord.data.products.map(({ sku }) => sku));
  const query = yield select(getQuery);
  const site = catalog?.site;
  const skuList = catalog?.products;
  const navigation = catalog?.navigation;
  const isLowerLevelCategory = navigation?.selected.length > 1;
  const pageSize = site?.listView.allowedProductsPerPage.find(page => page.current)?.numberOfProducts || 48;
  const page = site?.page || 1;
  const shouldIncludeSearchData = isLowerLevelCategory || query;
  const eventSource = query || isLowerLevelCategory ? TEALIUM_PROD_LIST.search : TEALIUM_PROD_LIST.category;
  const analyticsData = createProductsListsAnalyticsEventData({
    data: productTiles,
    skuList,
    pageSize,
    page,
    pageType: eventSource,
  });
  const { viewType } = yield select(getCatalogSettings);

  yield put(analyzeEvent({
    ...analyticsData,
    ...shouldIncludeSearchData && ({
      oss: getSearchAnalyticsOssData({
        ...OSS_INTERACTION_PARAMETERS[TEALIUM_INTERACTION_ID.searchPageView],
        query: '',
        resultsPerPage: pageSize,
        page,
        listType: viewType || PRODUCT_BOX_STYLE_TYPE.list,
        productsCount: catalogRecord.data.productsCount,
      }),
    }),
  }));
}

function* analyzeCategoryProductInteraction(
  interaction: AnalyzeProductsInteraction,
  sku: string,
  listPosition: number,
  quantity?: number,
):
  Generator<
    CallEffect | SelectEffect,
    void,
    RemoteRecord<ProductTileType> & RemoteRecord<CatalogSearchType> & SettingsCatalogType
  > {
  const productTileRecord = yield select(getProductTileBySku, sku);
  const productTile = productTileRecord.data;
  const catalogRecord = yield select(getSearch);
  const catalog = catalogRecord.data;

  if (!productTile || !catalog) {
    return null;
  }

  const { viewType } = yield select(getCatalogSettings);
  const { navigation, site } = catalog;
  const numberOfProducts = site?.listView.allowedProductsPerPage.find(page => page.current)?.numberOfProducts;
  const pageSize = numberOfProducts || DEFAULT_PER_PAGE;
  const page = site?.page || 1;
  const isLowerLevelCategory = navigation.selected.length > 1;

  yield call(
    interaction,
    isLowerLevelCategory ? TEALIUM_PROD_LIST.search : TEALIUM_PROD_LIST.category,
    {
      products: [{ ...getInteractionProduct(productTile), listPosition, quantity }],
      ...isLowerLevelCategory && {
        search: {
          query: '',
          page,
          resultsPerPage: pageSize,
          listType: viewType || PRODUCT_BOX_STYLE_TYPE.list,
          productsCount: catalog.productsCount,
        },
      },
    },
  );
}

export function* analyzeCategoryProductClick(sku: string, listPosition: number): Generator<CallEffect | void> {
  yield call(
    analyzeCategoryProductInteraction,
    analyzeProductClickInteraction,
    sku,
    listPosition,
  );
}

export function* analyzeCategoryProductAddToCart(sku: string, listPosition: number): Generator<CallEffect, void> {
  yield call(
    analyzeCategoryProductInteraction,
    analyzeProductAddToCartFromListInteraction,
    sku,
    listPosition,
    1,
  );
}
